import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, COUNTRIES } from "@/lib/categories";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSiteInfo, isComingSoon } from "@/hooks/useSiteInfo";
import { WaitlistDialog } from "@/components/ComingSoonRibbon";
import { X, Upload, Plus } from "lucide-react";
import { toast } from "sonner";

export default function PublishAdPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("editar");
  const info = useSiteInfo();
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const comingSoon = isComingSoon(info);
  const isAdmin = user && user.role === "admin";

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    price: "",
    city: "",
    country: "España",
    location: "",
    contact_email: "",
    contact_phone: "",
    images: [],
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authLoading && !(user && user.id)) {
      toast.error("Inicia sesión para publicar un anuncio");
      navigate("/");
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user && user.email && !form.contact_email) {
      setForm((f) => ({ ...f, contact_email: user.email, contact_phone: user.phone || "" }));
    }
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (editId) {
      api.get(`/ads/${editId}`).then((r) => {
        const a = r.data;
        setForm({
          title: a.title,
          description: a.description,
          category: a.category,
          price: a.price,
          city: a.city,
          country: a.country || "España",
          location: a.location || "",
          contact_email: a.contact_email,
          contact_phone: a.contact_phone || "",
          images: a.images || [],
        });
      });
    }
  }, [editId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 5 - form.images.length;
    if (remaining <= 0) {
      toast.error("Máximo 5 imágenes");
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.path);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
      toast.success(`${uploaded.length} imagen(es) subida(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error subiendo imagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (uploading) {
      setError("Espera a que terminen de subirse las imágenes");
      return;
    }
    if (!form.category) return setError("Elige una categoría");
    if (!form.city.trim()) return setError("Indica la ciudad");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        location: form.location.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
      };
      let res;
      if (editId) {
        res = await api.put(`/ads/${editId}`, payload);
      } else {
        res = await api.post("/ads", payload);
      }
      toast.success(editId ? "Anuncio actualizado" : "Anuncio publicado");
      navigate(`/anuncio/${res.data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error al publicar");
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !(user && user.id)) return null;

  // In coming-soon mode, non-admin users see a friendly locked screen instead of the form
  if (comingSoon && !isAdmin) {
    return (
      <div className="bg-[#F8FAFC] min-h-[70vh] flex items-center justify-center px-5">
        <div className="max-w-lg w-full bg-white border border-[#E2E8F0] p-10 text-center" data-testid="publish-locked">
          <p className="eyebrow mb-2">Muy pronto</p>
          <h1 className="font-serif text-3xl font-semibold text-[#1A1A1A]">Publicar aún no está abierto</h1>
          <p className="mt-3 text-[#475569]">
            Estamos ultimando detalles antes de abrir la comunidad. Déjanos tu email y serás de los primeros en publicar en cuanto arranque.
          </p>
          <button
            onClick={() => setWaitlistOpen(true)}
            data-testid="publish-locked-waitlist"
            className="mt-6 inline-flex items-center justify-center bg-[#1E3A8A] hover:bg-[#152B6B] text-[#F8FAFC] px-6 h-12 font-medium"
          >
            Únete a la lista de espera
          </button>
        </div>
        <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} contactEmail={info?.contact_email} />
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <p className="eyebrow mb-2">{editId ? "Editar anuncio" : "Publicar anuncio"}</p>
        <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-[#1A1A1A] mb-4">
          {editId ? "Actualiza tu anuncio" : "Crea un anuncio claro y directo"}
        </h1>

        {!editId && (
          <div
            data-testid="publish-lifecycle-notice"
            className="mb-8 border border-[#A3B8DA] bg-[#EEF4FB] p-4 flex items-start gap-3"
          >
            <div className="w-8 h-8 flex-shrink-0 bg-white border border-[#A3B8DA] flex items-center justify-center">
              <svg className="w-4 h-4 text-[#1E3A8A]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="text-sm text-[#0A1930] leading-relaxed">
              <strong className="text-[#1E3A8A]">Tu anuncio estará publicado durante 7 días.</strong>{" "}
              Pasada la semana, podrás renovarlo con un solo clic desde tu área "Mis anuncios" si quieres seguir compartiéndolo. Si no lo renuevas, se retirará automáticamente para mantener la comunidad al día.
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-8" data-testid="publish-form">
          {/* Sección */}
          <section className="bg-white border border-[#E2E8F0] p-6 space-y-4">
            <p className="eyebrow">Sección del anuncio</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  data-testid={`form-category-${c.slug}`}
                  onClick={() => set("category", c.slug)}
                  className={`h-12 border text-sm font-medium transition-colors ${
                    form.category === c.slug
                      ? "bg-[#1A1A1A] text-[#F8FAFC] border-[#1A1A1A]"
                      : "border-[#CBD5E1] text-[#1A1A1A] hover:border-[#3B5FB8]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          {/* Detalles */}
          <section className="bg-white border border-[#E2E8F0] p-6 space-y-4">
            <p className="eyebrow">Detalles</p>
            <div>
              <Label>Título</Label>
              <Input
                data-testid="form-title"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                required
                minLength={3}
                maxLength={140}
                placeholder="Un título claro y descriptivo"
                className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                data-testid="form-description"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                required
                minLength={10}
                maxLength={4000}
                rows={7}
                placeholder="Describe el servicio, condiciones, ventajas..."
                className="rounded-none border-[#CBD5E1] mt-1.5"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Precio</Label>
                <Input
                  data-testid="form-price"
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                  required
                  placeholder="p. ej. 20€ / plaza"
                  className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
                />
              </div>
              <div>
                <Label>Detalle ubicación (opcional)</Label>
                <Input
                  data-testid="form-location"
                  value={form.location}
                  onChange={(e) => set("location", e.target.value)}
                  placeholder="Barrio, universidad, referencia..."
                  className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
                />
              </div>
            </div>
          </section>

          {/* Localización */}
          <section className="bg-white border border-[#E2E8F0] p-6 space-y-4">
            <p className="eyebrow">Localización</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Ciudad</Label>
                <Input
                  data-testid="form-city"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  required
                  placeholder="p. ej. Madrid"
                  className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
                />
              </div>
              <div>
                <Label>País</Label>
                <Select value={form.country} onValueChange={(v) => set("country", v)}>
                  <SelectTrigger data-testid="form-country" className="rounded-none h-11 border-[#CBD5E1] mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-72">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Contacto */}
          <section className="bg-white border border-[#E2E8F0] p-6 space-y-4">
            <p className="eyebrow">Contacto</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Email de contacto</Label>
                <Input
                  data-testid="form-email"
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set("contact_email", e.target.value)}
                  required
                  className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
                />
              </div>
              <div>
                <Label>Teléfono (opcional)</Label>
                <Input
                  data-testid="form-phone"
                  value={form.contact_phone}
                  onChange={(e) => set("contact_phone", e.target.value)}
                  placeholder="+34 600 000 000"
                  className="rounded-none h-11 border-[#CBD5E1] mt-1.5"
                />
              </div>
            </div>
          </section>

          {/* Imágenes */}
          <section className="bg-white border border-[#E2E8F0] p-6 space-y-4">
            <div className="flex items-baseline justify-between">
              <p className="eyebrow">Imágenes (hasta 5)</p>
              <span className="text-xs text-[#64748B]">{form.images.length}/5</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {form.images.map((img, i) => (
                <div key={img} className="relative aspect-square border border-[#E2E8F0] overflow-hidden">
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    data-testid={`remove-image-${i}`}
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-7 h-7 bg-white/90 border border-[#E2E8F0] flex items-center justify-center hover:bg-white"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {form.images.length < 5 && (
                <label
                  data-testid="upload-image-button"
                  className="aspect-square border border-dashed border-[#CBD5E1] flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#3B5FB8] hover:bg-[#EEF2F7] transition-colors"
                >
                  {uploading ? (
                    <span className="text-xs text-[#475569]">Subiendo...</span>
                  ) : (
                    <>
                      <Plus className="w-5 h-5 text-[#3B5FB8]" />
                      <span className="text-xs font-mono uppercase tracking-wider text-[#475569]">Añadir</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </section>

          {error && (
            <p data-testid="form-error" className="text-sm text-red-700 border border-red-200 bg-red-50 px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              onClick={() => navigate(-1)}
              variant="outline"
              className="rounded-none h-12 px-6 border-[#CBD5E1]"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="submit-ad"
              disabled={submitting || uploading}
              className="rounded-none h-12 px-8 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] font-medium tracking-wide disabled:opacity-60"
            >
              {uploading ? "Subiendo imágenes..." : submitting ? "Guardando..." : editId ? "Guardar cambios" : "Publicar anuncio"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
